<template>
    <div class="full-height">
        <div class="row justify-end items-center bg-primary q-px-md q-py-sm">
            <q-btn-group flat v-if="!$q.platform.is.mobile">
                <q-btn
                    id="download-csv-button"
                    @click="exportDataAsCSV"
                    :loading="isDownloadingCSV"
                    class="download-file-buttons"
                    icon="far fa-file-csv"
                    text-color="white"
                    size="12px"
                    flat
                >
                    <q-tooltip style="font-size: 14px">
                        {{ $t('Export raw data (CSV)') }}
                        <br />
                        {{
                            $t(
                                'Filtered elements will not be present on the downloadable report.'
                            )
                        }}
                    </q-tooltip>
                </q-btn>
                <q-btn
                    id="download-xls-button"
                    @click="exportDataAsXLS"
                    :loading="isDownloadingXLS"
                    text-color="white"
                    class="download-file-buttons"
                    icon="far fa-file-excel"
                    size="12px"
                    flat
                >
                    <q-tooltip style="font-size: 14px">
                        {{ $t('Export raw data (XLS)') }}
                        <br />
                        {{
                            $t(
                                'Filtered elements will not be present on the downloadable report.'
                            )
                        }}
                    </q-tooltip>
                </q-btn>
            </q-btn-group>
        </div>

        <!-- Table mode -->
        <maintenance-report-table :report="report" />
    </div>
</template>

<script>
import { getLocaleFormat } from '@models/timeformats.js';
import FileExporter from '@utils/FileExporter.js';
import { logFileDownloadMessage } from '@utils/logMessagesForE2ETesting.js';
import { mapGetters } from 'vuex';
import MaintenanceReportTable from './MaintenanceReportTable.vue';
import { buildTable } from './maintenanceReport.js';
import { mapState } from 'pinia';
import { useReportsStore } from '@stores/reports.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'MaintenanceReport',
    components: { MaintenanceReportTable },
    props: {
        records: {
            required: true,
            validator(val) {
                return Array.isArray(val) || val === null;
            }
        },
        facilityId: {
            required: true
        },
        selectedAssets: {
            type: Set,
            required: true
        }
    },
    created() {
        // initialize file exporter instance
        this.fileExporter = new FileExporter({
            headers: this.fileHeaders
        });
    },
    data() {
        return {
            // button loading animation
            isDownloadingCSV: false,
            isDownloadingXLS: false,

            // file exporter class
            fileExporter: null
        };
    },
    computed: {
        ...mapGetters({
            getAsset: 'facilities/getAsset',
            getFacility: 'facilities/getFacility'
        }),
        ...mapState(useReportsStore, {
            getDateRange: 'dateRange'
        }),
        ...mapState(useAuthStore, {
            userLocale: 'getUserLocale'
        }),
        /**
         * Get the file headers for file exporting
         * @return {Array} file headers for file exporting
         */
        fileHeaders() {
            return [
                // match the columns in the fileRows
                this.$t('Facility'),
                this.$t('Asset'),
                this.$t('Cause'),
                this.$t('Cause code'),
                this.$t('Equipment failure'),
                this.$t('Running (min)'),
                this.$t('Total downtime (min)'),
                this.$t('Equipment failure duration (min)'),
                this.$t('Number of downtimes'),
                this.$t('Number of equipment failures'),
                this.$t('MTTR (min)'),
                this.$t('MTBF (min)'),
                this.$t('Report Start Date').concat(` [${this.localTimezone}]`),
                this.$t('Report End Date').concat(` [${this.localTimezone}]`)
            ];
        },
        /**
         * Build report data of current interval
         * @return {Object} report data containing table data and date interval
         */
        report() {
            const tableData = buildTable(this.records, this.filters);
            // // sort the rows for the array.
            tableData.rows.sort(function (a, b) {
                let firstAssetName = a.asset_name.toLowerCase();
                let secondAssetName = b.asset_name.toLowerCase();
                return firstAssetName.localeCompare(secondAssetName);
            });
            return tableData;
        },
        /**
         * All possible filters for this report
         * @return {Object} all possible filters for this report
         */
        filters() {
            return {
                assetIds: this.selectedAssets
            };
        },
        /**
         * Extract facility name from facility id
         * @return {String} facility name
         */
        facilityName() {
            return this.getFacility(this.facilityId).name.toLowerCase();
        },
        /**
         * Get local timezone
         * @return {String} local timezone
         */
        localTimezone() {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (timezone) {
                return timezone;
            }
            return 'UTC';
        },
        /**
         * Get the file rows for file exporting
         * @return {Array} file rows for exporting
         */
        /**
         * Generates formatted rows for the maintenance report, including individual records and asset totals.
         * @returns {Array} An array of formatted rows with maintenance data and calculated asset totals.
         */
        fileRows() {
            // Filter records by selected assets
            const filteredRecords = this.records.filter(record =>
                this.filters.assetIds.has(record['asset_id'])
            );

            const rows = [];
            const assetTotalsMap = new Map();

            // Process all records in a single loop
            for (const record of filteredRecords) {
                const assetId = record['asset_id'];
                const asset = this.getAsset(assetId);
                const isDeleted = asset ? asset.is_deleted || false : false;
                const facilityName = this.getFacility(
                    record['facility_id']
                ).name;
                const assetName = !isDeleted
                    ? record['asset_name']
                    : this.$t('{assetName} (Deleted)', {
                          assetName: record['asset_name']
                      });

                // Create row for individual record
                rows.push([
                    facilityName,
                    assetName,
                    record['cause_name'],
                    record['cause_code'],
                    record['equipment_failure'],
                    record['running_duration_min'],
                    record['downtime_duration_min'],
                    record['equipment_failure_duration_min'],
                    record['number_downtimes'],
                    record['number_equipment_failures'],
                    record['mttr_min'],
                    record['mtbf_min'],
                    this.formatDate(this.getDateRange.start),
                    this.formatDate(this.getDateRange.end)
                ]);

                // Initialize or update asset totals
                if (!assetTotalsMap.has(assetId)) {
                    assetTotalsMap.set(assetId, {
                        facilityName,
                        assetName,
                        running_duration_min:
                            record['running_duration_min'] || 0,
                        downtime_duration_min:
                            record['downtime_duration_min'] || 0,
                        equipment_failure_duration_min:
                            record['equipment_failure_duration_min'] || 0,
                        number_downtimes: record['number_downtimes'] || 0,
                        number_equipment_failures:
                            record['number_equipment_failures'] || 0
                    });
                } else {
                    const totals = assetTotalsMap.get(assetId);
                    // We don't accumulate running_duration_min as it should be counted once per asset
                    totals.downtime_duration_min +=
                        record['downtime_duration_min'] || 0;
                    totals.equipment_failure_duration_min +=
                        record['equipment_failure_duration_min'] || 0;
                    totals.number_downtimes += record['number_downtimes'] || 0;
                    totals.number_equipment_failures +=
                        record['number_equipment_failures'] || 0;
                }
            }

            // Add total rows for each asset
            for (const totals of assetTotalsMap.values()) {
                // Calculate MTTR and MTBF
                const mttr =
                    totals.number_equipment_failures > 0
                        ? totals.equipment_failure_duration_min /
                          totals.number_equipment_failures
                        : 0;

                const mtbf =
                    totals.number_equipment_failures > 0
                        ? totals.running_duration_min /
                          totals.number_equipment_failures
                        : 0;

                // Add the totals row
                rows.push([
                    totals.facilityName,
                    totals.assetName,
                    this.$t('Asset total'), // Indicate this is a total row
                    '', // No cause code for totals
                    '', // No equipment failure for totals
                    totals.running_duration_min,
                    totals.downtime_duration_min,
                    totals.equipment_failure_duration_min,
                    totals.number_downtimes,
                    totals.number_equipment_failures,
                    mttr,
                    mtbf,
                    this.formatDate(this.getDateRange.start),
                    this.formatDate(this.getDateRange.end)
                ]);
            }

            return rows;
        }
    },
    methods: {
        /**
         * Format a date to local date format
         * @param {String} datetime
         * @return {String} datetime in given locale.
         */
        formatDate(datetime) {
            return getLocaleFormat(datetime, this.userLocale);
        },
        /**
         * Export raw records as CSV.
         */
        exportDataAsCSV() {
            // begin loading
            this.isDownloadingCSV = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                this.fileExporter.exportCSV({
                    filename: `${this.facilityName}_maintenance_${start_date}_${end_date}`,
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('CSV');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingCSV = false;
        },
        /**
         * Export raw records as XLS.
         */
        exportDataAsXLS() {
            // begin loading
            this.isDownloadingXLS = true;

            const start_date = this.formatDate(this.getDateRange.start);
            const end_date = this.formatDate(this.getDateRange.end);
            try {
                this.fileExporter.exportXLS({
                    filename: `${this.facilityName}_maintenance_${start_date}_${end_date}`,
                    sheetName: 'Performance Report',
                    rows: this.fileRows
                });
                // log message for E2E testing
                logFileDownloadMessage('XLS');
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'An error has occurred during file export. Please try again.'
                    )
                );
            }
            // end loading
            this.isDownloadingXLS = false;
        }
    }
};
</script>

<style lang="scss" scoped></style>
